import type { KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Database,
  Search,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import {
  getOnboardingStepIndex,
  ONBOARDING_FLOW_STEPS,
} from './onboardingFlow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWorkspaceSidebar } from '@/components/layout/WorkspaceSidebarContext';
import BrandLogo from '@/components/common/BrandLogo';

const STEP_ICONS: Record<string, LucideIcon> = {
  configure: Settings,
  scan: Search,
  inventory: Database,
};

const OnboardingSidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentIndex = getOnboardingStepIndex(pathname);
  const { closeMobile } = useWorkspaceSidebar() ?? {};

  const getStepState = (index: number): 'completed' | 'active' | 'upcoming' => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'active';
    return 'upcoming';
  };

  const handleStepClick = (index: number, path: string) => {
    if (index >= currentIndex) return;
    navigate(path);
    closeMobile?.();
  };

  return (
    <aside
      className="box-border flex h-full min-h-full w-full flex-col bg-[var(--inv-bg)] px-4 py-5"
      aria-label="Onboarding progress">
      <div className="box-border flex h-[var(--workspace-header-height,2.75rem)] shrink-0 items-center">
        <BrandLogo />
      </div>

      <nav className="flex flex-col gap-[0.35rem] pt-6">
        <Button
          type="button"
          variant="ghost"
          className="mt-2 h-auto w-full justify-start gap-[0.45rem] rounded-lg px-[0.65rem] py-2 text-left text-[0.8125rem] font-medium text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]"
          onClick={() => {
            navigate('/sia/components');
            closeMobile?.();
          }}>
          <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Back to components</span>
        </Button>

        <div className="mb-[0.15rem] mt-3 px-[0.55rem] text-[0.5625rem] font-bold uppercase tracking-[0.12em] text-[var(--inv-text-muted)]">
          Onboarding
        </div>

        <div
          className="mt-3 flex flex-col px-1"
          role="list"
          aria-label="Onboarding steps">
          {ONBOARDING_FLOW_STEPS.map((step, index) => {
            const state = getStepState(index);
            const isClickable = state === 'completed';
            const isLast = index === ONBOARDING_FLOW_STEPS.length - 1;
            const StepIcon = STEP_ICONS[step.id] ?? Settings;

            return (
              <div
                key={step.id}
                role="listitem"
                className={cn(
                  'flex w-full gap-[0.65rem] border-0 bg-transparent p-0 text-left',
                  isClickable ? 'group cursor-pointer' : 'cursor-default',
                )}
                onClick={
                  isClickable
                    ? () => handleStepClick(index, step.path)
                    : undefined
                }
                onKeyDown={
                  isClickable
                    ? (e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleStepClick(index, step.path);
                        }
                      }
                    : undefined
                }
                tabIndex={isClickable ? 0 : undefined}
                aria-current={state === 'active' ? 'step' : undefined}>
                <div className="flex w-6 shrink-0 flex-col items-center">
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-solid text-[0.6875rem] font-bold leading-none',
                      state === 'active' &&
                        'border-[var(--inv-accent)] bg-[var(--inv-accent)] text-[var(--inv-on-accent)]',
                      state === 'completed' &&
                        'border-[var(--inv-accent)] bg-[var(--inv-accent-soft)] text-[var(--inv-accent)]',
                      state === 'upcoming' &&
                        'border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text-muted)]',
                    )}
                    aria-hidden="true">
                    {state === 'completed' ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  {!isLast ? (
                    <span
                      className={cn(
                        'my-[0.2rem] min-h-4 w-0.5 flex-1',
                        state === 'completed'
                          ? 'bg-[var(--inv-accent-soft)]'
                          : 'bg-[var(--inv-border-soft)]',
                      )}
                    />
                  ) : null}
                </div>

                <div
                  className={cn(
                    'min-w-0 flex-1 rounded-lg px-[0.55rem] pb-[0.85rem] pt-[0.15rem] transition-colors',
                    isClickable &&
                      'group-hover:bg-[var(--inv-accent-ring)] focus-within:bg-[var(--inv-accent-ring)]',
                  )}>
                  <div className="flex items-center gap-[0.35rem]">
                    <StepIcon
                      className={cn(
                        'h-[0.85rem] w-[0.85rem] shrink-0',
                        state === 'active'
                          ? 'text-[var(--inv-accent)]'
                          : 'text-[var(--inv-text-tertiary)]',
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={cn(
                        'text-[0.8125rem] font-medium leading-snug',
                        state === 'active' &&
                          'font-semibold text-[var(--inv-accent)]',
                        state === 'completed' && 'text-[var(--inv-text)]',
                        state === 'upcoming' && 'text-[var(--inv-text-muted)]',
                      )}>
                      {step.label}
                    </span>
                  </div>
                  <p
                    className={cn(
                      'm-0 mt-[0.2rem] text-[0.6875rem] leading-[1.45] text-[var(--inv-text-secondary)]',
                      state === 'upcoming' && 'text-[var(--inv-text-muted)] opacity-85',
                    )}>
                    {step.description}
                  </p>
                  {state === 'active' ? (
                    <Badge
                      variant="backend"
                      className="mt-1 px-0 py-0 text-[0.625rem] tracking-[0.04em]">
                      In progress
                    </Badge>
                  ) : null}
                  {state === 'completed' ? (
                    <Badge
                      variant="backend"
                      className="mt-1 px-0 py-0 text-[0.625rem] tracking-[0.04em]">
                      Completed
                    </Badge>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    </aside>
  );
};

export default OnboardingSidebar;
